import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../contexts/ThemeContext';
import Footer from '../components/Footer';
import { formatCurrency, formatNumber, formatIndianNumber } from '../utils/formatters';

const { width } = Dimensions.get('window');

const SimpleInterestCalculatorScreen = ({ navigation }) => {
  const { colors, isDarkMode } = useTheme();
  
  // State variables
  const [calculatorType, setCalculatorType] = useState('simple');
  const [results, setResults] = useState(null);
  const [showFAQ, setShowFAQ] = useState(true);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [showFrequencyDropdown, setShowFrequencyDropdown] = useState(false);

  // Screen dimensions for responsive design
  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  const isWeb = Platform.OS === 'web';
  const isMobile = screenData.width < 900;

  // Update screen dimensions on resize
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenData(window);
    });
    return () => subscription?.remove();
  }, []);
  
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isCalculated, setIsCalculated] = useState(false);
  
  const [formData, setFormData] = useState({
    principal: '10000',
    rate: '10',
    time: '1',
    timeUnit: 'years', // 'years' or 'months'
    compoundFrequency: 'annually', // Default compound frequency
  });
  
  const timeUnits = [
    { value: 'years', label: 'Years' },
    { value: 'months', label: 'Months' },
  ];

  const compoundFrequencies = [
    { value: 'annually', label: 'Annually' },
    { value: 'semi-annually', label: 'Semi-Annually' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'daily', label: 'Daily' },
  ];

  const calculatorTypes = [
    { value: 'simple', label: 'Simple Interest' },
    { value: 'compound', label: 'Compound Interest' },
  ];

  // Parse comma-formatted currency value
  const parseCurrency = (value) => {
    if (!value) return 0;
    return parseFloat(value.toString().replace(/[₹,\s]/g, '')) || 0;
  };

  // Format input with commas
  const formatInput = (value) => {
    if (!value) return '';
    const numericValue = value.replace(/[^\d.]/g, '');
    if (numericValue === '') return '';
    return formatIndianNumber(parseFloat(numericValue));
  };

  // Calculate interest in real-time
  useEffect(() => {
    if (formData.principal && formData.rate && formData.time && !isNaN(parseFloat(formData.principal)) && !isNaN(parseFloat(formData.rate)) && !isNaN(parseFloat(formData.time))) {
      calculateInterest();
    } else {
      setResults(null);
    }
  }, [formData.principal, formData.rate, formData.time, formData.timeUnit, calculatorType, formData.compoundFrequency]);

  const calculateInterest = () => {
    if (!formData.principal || !formData.rate || !formData.time || isNaN(formData.principal) || isNaN(formData.rate) || isNaN(formData.time)) {
      setResults(null);
      return;
    }

    const P = parseFloat(formData.principal);
    const R = parseFloat(formData.rate);
    let T = parseFloat(formData.time);
    
    // Convert time to years if needed
    if (formData.timeUnit === 'months') {
      T = T / 12;
    }

    let interest, totalAmount;

    if (calculatorType === 'simple') {
      // Simple Interest: SI = (P × R × T) / 100
      interest = (P * R * T) / 100;
      totalAmount = P + interest;
    } else {
      // Compound Interest: CI = P(1 + R/100)^T - P
      let n = 1; // Default to annually
      
      switch (formData.compoundFrequency) {
        case 'semi-annually':
          n = 2;
          break;
        case 'quarterly':
          n = 4;
          break;
        case 'monthly':
          n = 12;
          break;
        case 'daily':
          n = 365;
          break;
        default:
          n = 1;
      }
      
      totalAmount = P * Math.pow((1 + R / (100 * n)), n * T);
      interest = totalAmount - P;
    }

    setResults({
      principal: Math.round(P * 100) / 100,
      interest: Math.round(interest * 100) / 100,
      totalAmount: Math.round(totalAmount * 100) / 100,
      rate: R,
      time: formData.time,
      timeUnit: formData.timeUnit,
      calculatorType: calculatorType,
      compoundFrequency: calculatorType === 'compound' ? formData.compoundFrequency : null
    });
  };

  const renderHeader = () => (
    <View style={styles.header}>
      {isMobile && (
        <TouchableOpacity 
          style={styles.hamburgerButton}
          onPress={() => navigation.openDrawer?.()}
        >
          <Ionicons name="menu" size={24} color="white" />
        </TouchableOpacity>
      )}
      <Text style={styles.headerTitle}>Simple & Compound Interest Calculator</Text>
      <Text style={styles.headerSubtitle}>
        Calculate simple interest on your investments and loans with our easy-to-use calculator. Get instant results for principal, interest, and total amount. Perfect for planning your financial goals and understanding loan payments.
      </Text>
      
      <View style={styles.headerTags}>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>Instant Results</Text>
        </View>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>Easy Formula</Text>
        </View>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>Loan Planning</Text>
        </View>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>100% Free</Text>
        </View>
      </View>
    </View>
  );

  const renderCalculatorTypeSelector = () => (
    <View style={styles.inputGroup}>
      <View style={styles.userTypeContainer}>
        {calculatorTypes.map((type) => (
          <TouchableOpacity
            key={type.value}
            style={[
              styles.userTypeButton,
              calculatorType === type.value && styles.userTypeButtonSelected
            ]}
            onPress={() => setCalculatorType(type.value)}
          >
            <View style={[
              styles.radioButton,
              calculatorType === type.value && styles.radioButtonSelected
            ]}>
              {calculatorType === type.value && <View style={styles.radioButtonInner} />}
            </View>
            <Text style={styles.userTypeText}>{type.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  const renderPrincipalInput = () => (
    <View style={styles.inputGroup}>
      <Text style={styles.label}>Principal Amount (₹)</Text>
      <View style={styles.inputContainer}>
        <Text style={styles.currencySymbol}>₹</Text>
        <TextInput
          style={styles.input}
          value={formData.principal}
          onChangeText={(value) => handleInputChange('principal', value)}
          placeholder="10000"
          placeholderTextColor={colors.textSecondary}
          keyboardType="numeric"
        />
      </View>
    </View>
  );

  const renderRateInput = () => (
    <View style={styles.inputGroup}>
      <Text style={styles.label}>Annual Interest Rate (%)</Text>
      <View style={styles.inputContainer}>
        <TextInput
          style={styles.input}
          value={formData.rate}
          onChangeText={(value) => handleInputChange('rate', value)}
          placeholder="10"
          placeholderTextColor={colors.textSecondary}
          keyboardType="numeric"
        />
        <Text style={styles.percentSymbol}>%</Text>
      </View>
    </View>
  );

  const renderTimeInput = () => (
    <View style={styles.inputGroup}>
      <Text style={styles.label}>Time Period</Text>
      <View style={styles.timeInputContainer}>
        <View style={styles.timeInputWrapper}>
          <TextInput
            style={styles.timeInput}
            value={formData.time}
            onChangeText={(value) => handleInputChange('time', value)}
            placeholder="1"
            placeholderTextColor={colors.textSecondary}
            keyboardType="numeric"
          />
        </View>
        <TouchableOpacity 
          style={styles.dropdownContainer}
          onPress={() => setShowTimeDropdown(!showTimeDropdown)}
        >
          <Text style={styles.dropdownValue}>{timeUnits.find(unit => unit.value === formData.timeUnit)?.label}</Text>
          <Ionicons 
            name={showTimeDropdown ? "chevron-up" : "chevron-down"} 
            size={20} 
            color={colors.textSecondary} 
          />
        </TouchableOpacity>
      </View>
      {showTimeDropdown && (
        <View style={styles.timeUnitOptions}>
          {timeUnits.map((unit) => (
            <TouchableOpacity
              key={unit.value}
              style={[
                styles.timeUnitOption,
                formData.timeUnit === unit.value && styles.timeUnitOptionSelected
              ]}
              onPress={() => {
                handleInputChange('timeUnit', unit.value);
                setShowTimeDropdown(false);
              }}
            >
              <Text style={[
                styles.timeUnitText,
                formData.timeUnit === unit.value && styles.timeUnitTextSelected
              ]}>
                {unit.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      )}
    </View>
  );

  const renderCompoundFrequencyDropdown = () => {
    if (calculatorType !== 'compound') return null;

    return (
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Compound Frequency</Text>
        <TouchableOpacity 
          style={styles.dropdownContainer}
          onPress={() => setShowFrequencyDropdown(!showFrequencyDropdown)}
        >
          <Text style={styles.dropdownValue}>
            {compoundFrequencies.find(freq => freq.value === formData.compoundFrequency)?.label}
          </Text>
          <Ionicons 
            name={showFrequencyDropdown ? "chevron-up" : "chevron-down"} 
            size={20} 
            color={colors.textSecondary} 
          />
        </TouchableOpacity>
        {showFrequencyDropdown && (
          <View style={styles.frequencyOptions}>
            {compoundFrequencies.map((freq) => (
              <TouchableOpacity
                key={freq.value}
                style={[
                  styles.frequencyOption,
                  formData.compoundFrequency === freq.value && styles.frequencyOptionSelected
                ]}
                onPress={() => {
                  handleInputChange('compoundFrequency', freq.value);
                  setShowFrequencyDropdown(false);
                }}
              >
                <Text style={[
                  styles.frequencyText,
                  formData.compoundFrequency === freq.value && styles.frequencyTextSelected
                ]}>
                  {freq.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>
    );
  };

  const renderDonutChart = () => {
    if (!results) return null;

    const { principal, interest } = results;
    const total = principal + interest;
    const principalPercentage = (principal / total) * 100;
    const interestPercentage = (interest / total) * 100;

    return (
      <View style={styles.chartContainer}>
        <View style={styles.donutChart}>
          <View style={styles.donutBase}>
            {/* Principal segment */}
            <View style={[styles.donutSegment, {
              borderTopColor: principalPercentage > 25 ? '#4CAF50' : 'transparent',
              borderRightColor: principalPercentage > 50 ? '#4CAF50' : 'transparent',
              borderBottomColor: principalPercentage > 75 ? '#4CAF50' : 'transparent',
              borderLeftColor: '#4CAF50',
              transform: [{ rotate: '0deg' }],
            }]} />
            {/* Interest segment */}
            <View style={[styles.donutSegment, {
              borderTopColor: interestPercentage > 25 ? '#FF6B6B' : 'transparent',
              borderRightColor: interestPercentage > 50 ? '#FF6B6B' : 'transparent',
              borderBottomColor: interestPercentage > 75 ? '#FF6B6B' : 'transparent',
              borderLeftColor: interestPercentage > 0 ? '#FF6B6B' : 'transparent',
              transform: [{ rotate: `${(principalPercentage / 100) * 360}deg` }],
            }]} />
          </View>
        </View>
        <View style={styles.chartLegend}>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: '#4CAF50' }]} />
            <Text style={styles.legendText}>Principal</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: '#FF6B6B' }]} />
            <Text style={styles.legendText}>Interest</Text>
          </View>
        </View>
      </View>
    );
  };

  useEffect(() => {
    calculateInterest();
  }, [formData]);

  const handleInputChange = (field, value) => {
    if (field === 'principal') {
      const formatted = formatInput(value);
      setFormData(prev => ({ ...prev, [field]: formatted }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleCalculate = () => {
    calculateInterest();
    setIsCalculated(true);
  };

  const renderCalculator = () => (
    <View style={styles.calculatorContainer}>
      <View style={styles.calculatorRow}>
        <View style={styles.formContent}>
          {renderCalculatorTypeSelector()}
          {renderPrincipalInput()}
          {renderRateInput()}
          {renderTimeInput()}
          {renderCompoundFrequencyDropdown()}
        </View>
        
        {!isMobile && (
          <View style={styles.summaryPanel}>
            <View style={styles.panelHeader}>
              <Text style={styles.panelTitle}>Interest Summary</Text>
              <Text style={styles.panelSubtitle}>Total Interest</Text>
              <Text style={styles.panelAmount}>
                ₹{formatIndianNumber(calculatorType === 'simple' ? (results?.interest || 0) : (results?.totalInterest || 0))}
              </Text>
            </View>
            
            <View style={styles.taxInfo}>
              <Text style={styles.infoTitle}>Principal Amount</Text>
              <Text style={styles.infoValue}>
                ₹{formatIndianNumber(parseCurrency(formData.principal))}
              </Text>
            </View>
            
            <View style={styles.taxInfo}>
              <Text style={styles.infoTitle}>Interest Rate</Text>
              <Text style={styles.infoValue}>{formData.rate}% per annum</Text>
            </View>
            
            <View style={styles.taxInfo}>
              <Text style={styles.infoTitle}>Time Period</Text>
              <Text style={styles.infoValue}>
                {formData.time} {formData.timeUnit}
              </Text>
            </View>
            
            <View style={styles.regimeInfo}>
              <Text style={styles.regimeTitle}>
                Total maturity amount will be ₹{formatIndianNumber(results?.totalAmount || 0)} 
                after {formData.time} {formData.timeUnit}.
              </Text>
            </View>
          </View>
        )}
      </View>
    </View>
  );

  const renderResults = () => {
    if (!results) {
      return (
        <View style={styles.resultsContainer}>
          <View style={styles.emptyResults}>
            <Ionicons name="calculator-outline" size={48} color={colors.textSecondary} />
            <Text style={styles.emptyResultsText}>Enter values to see interest calculation</Text>
          </View>
        </View>
      );
    }

    return (
      <View style={styles.resultsContainer}>
        <View style={styles.resultsList}>
          <View style={styles.resultRow}>
            <Text style={styles.resultLabel}>Principal Amount</Text>
            <Text style={styles.resultValue}>₹{results?.principal?.toLocaleString() || '0'}</Text>
          </View>
          
          <View style={styles.resultRow}>
            <Text style={styles.resultLabel}>
              {calculatorType === 'simple' ? 'Simple Interest' : 'Compound Interest'}
            </Text>
            <Text style={styles.resultValue}>₹{results?.interest?.toLocaleString() || '0'}</Text>
          </View>
          
          <View style={[styles.resultRow, styles.totalRow]}>
            <Text style={styles.totalLabel}>Total Amount</Text>
            <Text style={styles.totalValue}>₹{results?.totalAmount?.toLocaleString() || '0'}</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderInterestInformation = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.infoTitle}>What is Interest?</Text>
      <Text style={styles.infoText}>
        Interest is the cost of borrowing money or the reward for saving money. It's calculated as a percentage of the principal amount over a specific period. 
        Understanding interest helps you make informed financial decisions about loans, investments, and savings.
      </Text>

      <Text style={styles.infoSubtitle}>Simple Interest vs Compound Interest</Text>
      <Text style={styles.infoText}>
        <Text style={styles.boldText}>Simple Interest:</Text> Calculated only on the principal amount. The interest remains constant throughout the investment period.
      </Text>
      <Text style={styles.infoText}>
        <Text style={styles.boldText}>Compound Interest:</Text> Calculated on both the principal and previously earned interest. This creates a compounding effect where your money grows faster over time.
      </Text>

      <Text style={styles.infoSubtitle}>Interest Calculation Formulas</Text>
      <Text style={styles.infoText}>Simple Interest Formula:</Text>
      <View style={styles.formulaBox}>
        <Text style={styles.formulaText}>SI = (P × R × T) / 100</Text>
        <Text style={styles.formulaText}>Total Amount = P + SI</Text>
      </View>

      <Text style={styles.infoText}>Compound Interest Formula:</Text>
      <View style={styles.formulaBox}>
        <Text style={styles.formulaText}>CI = P(1 + R/100)^T - P</Text>
        <Text style={styles.formulaText}>Where: P = Principal, R = Rate, T = Time</Text>
      </View>

      <Text style={styles.infoSubtitle}>How to Use Interest Calculator?</Text>
      <View style={styles.stepsList}>
        <Text style={styles.stepText}>Step 1: Choose between Simple or Compound Interest</Text>
        <Text style={styles.stepText}>Step 2: Enter the principal amount you want to invest or borrow</Text>
        <Text style={styles.stepText}>Step 3: Input the annual interest rate percentage</Text>
        <Text style={styles.stepText}>Step 4: Specify the time period (years or months)</Text>
        <Text style={styles.stepText}>Step 5: For compound interest, select the compounding frequency</Text>
        <Text style={styles.stepText}>Step 6: View instant calculations with detailed breakdown</Text>
      </View>

      <Text style={styles.infoSubtitle}>Benefits of Using Interest Calculator</Text>
      <View style={styles.bulletList}>
        <Text style={styles.bulletPoint}>• Compare different investment options quickly</Text>
        <Text style={styles.bulletPoint}>• Plan your financial goals with accurate projections</Text>
        <Text style={styles.bulletPoint}>• Understand the impact of compounding on your investments</Text>
        <Text style={styles.bulletPoint}>• Make informed decisions about loans and mortgages</Text>
        <Text style={styles.bulletPoint}>• Save time with instant calculations and visual charts</Text>
      </View>
    </View>
  );

  const renderFAQSection = () => (
    <View style={styles.faqContainer}>
      <TouchableOpacity 
        style={styles.faqHeader}
        onPress={() => setShowFAQ(!showFAQ)}
      >
        <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
        <Ionicons 
          name={showFAQ ? "chevron-up" : "chevron-down"} 
          size={24} 
          color={colors.primary} 
        />
      </TouchableOpacity>
      
      {showFAQ && (
        <View style={styles.faqContent}>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: What's the difference between simple and compound interest?</Text>
            <Text style={styles.faqAnswer}>
              A: Simple interest is calculated only on the principal amount, while compound interest is calculated on both the principal and previously earned interest. Compound interest grows faster over time due to the compounding effect.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: How often should I choose compounding frequency?</Text>
            <Text style={styles.faqAnswer}>
              A: Higher compounding frequency (daily, monthly) results in higher returns. However, the difference becomes less significant for shorter time periods. For long-term investments, choose the highest frequency available.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: Can I use this calculator for loan calculations?</Text>
            <Text style={styles.faqAnswer}>
              A: Yes! This calculator works for both investments and loans. For loans, the interest calculated represents the amount you'll pay in addition to the principal amount borrowed.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: Is the calculation accurate for real-world scenarios?</Text>
            <Text style={styles.faqAnswer}>
              A: Our calculator provides mathematically accurate results based on the formulas. However, real-world factors like taxes, fees, and varying interest rates may affect actual returns or payments.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: What time period should I use for calculations?</Text>
            <Text style={styles.faqAnswer}>
              A: You can use any time period - years, months, or even days. The calculator automatically converts different time units to provide accurate results. For investments, longer periods show the power of compounding more clearly.
            </Text>
          </View>
        </View>
      )}
    </View>
  );

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {/* Hero Section - Same as Income Tax Calculator */}
      <View style={styles.header}>
        {isMobile && (
          <TouchableOpacity 
            style={styles.hamburgerButton}
            onPress={() => setSidebarVisible(!sidebarVisible)}
          >
            <Ionicons name="menu" size={24} color={colors.text} />
          </TouchableOpacity>
        )}
        <Text style={styles.headerTitle}>Simple Interest Calculator</Text>
        <Text style={styles.headerSubtitle}>
          Calculate simple interest on your investments and loans with our easy-to-use calculator. 
          Get instant results for principal, interest, and total amount. Perfect for planning your 
          financial goals and understanding loan payments.
        </Text>
        
        <View style={styles.headerTags}>
          <View style={styles.headerTag}>
            <Text style={styles.headerTagText}>Instant Results</Text>
          </View>
          <View style={styles.headerTag}>
            <Text style={styles.headerTagText}>Easy Formula</Text>
          </View>
          <View style={styles.headerTag}>
            <Text style={styles.headerTagText}>Loan Planning</Text>
          </View>
          <View style={styles.headerTag}>
            <Text style={styles.headerTagText}>100% Free</Text>
          </View>
        </View>
      </View>

      <View style={styles.mainContent}>
        <View style={styles.leftPanel}>
          {renderCalculator()}
          {renderResults()}
        </View>
      </View>

      {renderInterestInformation()}
      {renderFAQSection()}
      
      {/* Footer - Same as Income Tax Calculator */}
      <Footer />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: '#f8fafc',
    },
    header: {
      backgroundColor: '#6366f1',
      paddingHorizontal: 20,
      paddingVertical: 40,
      paddingTop: Platform.OS === 'web' ? 40 : 60,
    },
    hamburgerButton: {
      position: 'absolute',
      top: Platform.OS === 'web' ? 20 : 40,
      left: 20,
      zIndex: 1000,
      padding: 8,
      borderRadius: 8,
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    headerTitle: {
      fontSize: 32,
      fontWeight: 'bold',
      color: '#ffffff',
      marginBottom: 12,
      textAlign: 'center',
    },
    headerSubtitle: {
      fontSize: 16,
      color: '#e0e7ff',
      textAlign: 'center',
      lineHeight: 24,
      marginBottom: 24,
    },
    headerTags: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: 8,
    },
    headerTag: {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 16,
    },
    headerTagText: {
      color: '#ffffff',
      fontSize: 12,
      fontWeight: '500',
    },
    mainContent: {
      flexDirection: 'row',
      padding: 20,
      gap: 20,
      alignSelf: 'center',
      width: '100%',
    },
    leftPanel: {
      flex: 2,
    },
    rightPanel: {
      flex: 1,
      backgroundColor: '#ffffff',
      borderRadius: 12,
      padding: 20,
      height: 'fit-content',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    calculatorContainer: {
      backgroundColor: '#ffffff',
      borderRadius: 12,
      padding: 24,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    calculatorRow: {
      flexDirection: 'row',
      gap: 24,
    },
    summaryPanel: {
      flex: 0.3,
      backgroundColor: '#f8fafc',
      borderRadius: 12,
      padding: 20,
      height: 'fit-content',
      borderWidth: 1,
      borderColor: '#e2e8f0',
    },
    panelHeader: {
      marginBottom: 20,
    },
    panelTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      color: '#1f2937',
      marginBottom: 4,
    },
    panelSubtitle: {
      fontSize: 14,
      color: '#6b7280',
      marginBottom: 8,
    },
    panelAmount: {
      fontSize: 24,
      fontWeight: 'bold',
      color: '#059669',
    },
    taxInfo: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: '#e5e7eb',
    },
    infoTitle: {
      fontSize: 14,
      color: '#6b7280',
      flex: 1,
    },
    infoValue: {
      fontSize: 14,
      fontWeight: '600',
      color: '#1f2937',
      textAlign: 'right',
    },
    regimeInfo: {
      marginTop: 16,
      padding: 16,
      backgroundColor: '#f0f9ff',
      borderRadius: 8,
      borderLeftWidth: 4,
      borderLeftColor: '#0ea5e9',
    },
    regimeTitle: {
      fontSize: 13,
      color: '#0c4a6e',
      lineHeight: 18,
    },
    formContent: {
      gap: 20,
      flex: 0.7,
    },
    sectionTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: '#1f2937',
      marginBottom: 8,
    },
    sectionSubtitle: {
      fontSize: 14,
      color: '#6b7280',
      marginBottom: 24,
    },
    inputGroup: {
      marginBottom: 20,
    },
    label: {
      fontSize: 14,
      fontWeight: '600',
      color: '#374151',
      marginBottom: 8,
    },
    inputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: '#d1d5db',
      borderRadius: 8,
      backgroundColor: '#ffffff',
    },
    currencySymbol: {
      paddingLeft: 12,
      fontSize: 16,
      color: '#6b7280',
    },
    percentSymbol: {
      paddingRight: 12,
      fontSize: 16,
      color: '#6b7280',
    },
    input: {
      flex: 1,
      padding: 12,
      fontSize: 16,
      color: '#1f2937',
    },
    timeInputContainer: {
      flexDirection: 'row',
      gap: 12,
    },
    timeInputWrapper: {
      flex: 1,
    },
    timeInput: {
      borderWidth: 1,
      borderColor: '#d1d5db',
      borderRadius: 8,
      padding: 12,
      fontSize: 16,
      backgroundColor: '#ffffff',
      color: '#1f2937',
    },
    dropdownContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: '#d1d5db',
      borderRadius: 8,
      paddingHorizontal: 16,
      paddingVertical: 12,
      backgroundColor: '#ffffff',
      minWidth: 120,
    },
    dropdownValue: {
      fontSize: 16,
      color: '#1f2937',
    },
    timeUnitOptions: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: '#ffffff',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: '#d1d5db',
      marginTop: 4,
      zIndex: 1000,
      elevation: 5,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
    },
    timeUnitOption: {
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: '#f3f4f6',
    },
    timeUnitOptionSelected: {
      backgroundColor: '#f0f9ff',
    },
    timeUnitText: {
      fontSize: 16,
      color: '#1f2937',
    },
    timeUnitTextSelected: {
      color: '#6366f1',
      fontWeight: '600',
    },
    frequencyOptions: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: '#ffffff',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: '#d1d5db',
      marginTop: 4,
      zIndex: 1000,
      elevation: 5,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
    },
    frequencyOption: {
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: '#f3f4f6',
    },
    frequencyOptionSelected: {
      backgroundColor: '#f0f9ff',
    },
    frequencyText: {
      fontSize: 16,
      color: '#1f2937',
    },
    frequencyTextSelected: {
      color: '#6366f1',
      fontWeight: '600',
    },
    userTypeContainer: {
      flexDirection: 'row',
      gap: 16,
    },
    userTypeButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingVertical: 8,
    },
    userTypeButtonSelected: {
      // Selected state styling handled by radio button
    },
    radioButton: {
      width: 20,
      height: 20,
      borderRadius: 10,
      borderWidth: 2,
      borderColor: '#d1d5db',
      alignItems: 'center',
      justifyContent: 'center',
    },
    radioButtonSelected: {
      borderColor: '#6366f1',
    },
    radioButtonInner: {
      width: 10,
      height: 10,
      borderRadius: 5,
      backgroundColor: '#6366f1',
    },
    userTypeText: {
      fontSize: 16,
      color: '#1f2937',
    },
    resultsContainer: {
      backgroundColor: '#ffffff',
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    emptyResults: {
      alignItems: 'center',
      paddingVertical: 40,
    },
    emptyResultsText: {
      fontSize: 16,
      color: '#6b7280',
      marginTop: 12,
    },
    chartContainer: {
      alignItems: 'center',
      marginBottom: 24,
    },
    donutChart: {
      width: 200,
      height: 200,
      justifyContent: 'center',
      alignItems: 'center',
    },
    donutBase: {
      width: 200,
      height: 200,
      borderRadius: 100,
      position: 'relative',
    },
    donutSegment: {
      position: 'absolute',
      width: 200,
      height: 200,
      borderRadius: 100,
      borderWidth: 30,
    },
    chartLegend: {
      flexDirection: 'row',
      justifyContent: 'center',
      gap: 20,
      marginTop: 16,
    },
    legendItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    legendColor: {
      width: 12,
      height: 12,
      borderRadius: 6,
    },
    legendText: {
      fontSize: 14,
      color: '#6b7280',
    },
    resultsList: {
      gap: 16,
    },
    resultRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: '#f3f4f6',
    },
    totalRow: {
      borderBottomWidth: 0,
      borderTopWidth: 2,
      borderTopColor: '#6366f1',
      paddingTop: 16,
      marginTop: 8,
    },
    resultLabel: {
      fontSize: 16,
      color: '#6b7280',
    },
    resultValue: {
      fontSize: 16,
      fontWeight: '600',
      color: '#1f2937',
    },
    totalLabel: {
      fontSize: 18,
      fontWeight: 'bold',
      color: '#1f2937',
    },
    totalValue: {
      fontSize: 18,
      fontWeight: 'bold',
      color: '#6366f1',
    },
    infoContainer: {
      backgroundColor: '#ffffff',
      borderRadius: 12,
      padding: 24,
      margin: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    infoTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: '#1f2937',
      marginBottom: 16,
    },
    infoSubtitle: {
      fontSize: 18,
      fontWeight: '600',
      color: '#1f2937',
      marginTop: 24,
      marginBottom: 12,
    },
    infoText: {
      fontSize: 16,
      color: '#6b7280',
      lineHeight: 24,
      marginBottom: 12,
    },
    boldText: {
      fontWeight: '600',
      color: '#1f2937',
    },
    formulaBox: {
      backgroundColor: '#f8fafc',
      borderRadius: 8,
      padding: 16,
      marginVertical: 12,
      borderLeftWidth: 4,
      borderLeftColor: '#6366f1',
    },
    formulaText: {
      fontSize: 16,
      fontFamily: 'monospace',
      color: '#1f2937',
      marginBottom: 4,
    },
    stepsList: {
      marginTop: 12,
    },
    stepText: {
      fontSize: 16,
      color: '#6b7280',
      lineHeight: 24,
      marginBottom: 8,
    },
    bulletList: {
      marginTop: 12,
    },
    bulletPoint: {
      fontSize: 16,
      color: '#6b7280',
      lineHeight: 24,
      marginBottom: 8,
    },
    panelHeader: {
      alignItems: 'center',
      marginBottom: 24,
      paddingBottom: 16,
      borderBottomWidth: 1,
      borderBottomColor: '#e5e7eb',
    },
    panelTitle: {
      fontSize: 18,
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: 4,
    },
    panelSubtitle: {
      fontSize: 12,
      color: '#6b7280',
      marginBottom: 8,
    },
    panelAmount: {
      fontSize: 24,
      fontWeight: 'bold',
      color: '#059669',
    },
    taxInfo: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: '#f3f4f6',
    },
    infoValue: {
      fontSize: 14,
      fontWeight: '600',
      color: '#1f2937',
    },
    regimeInfo: {
      marginTop: 20,
      padding: 16,
      backgroundColor: '#f0f9ff',
      borderRadius: 8,
    },
    regimeTitle: {
      fontSize: 12,
      color: '#1e40af',
      lineHeight: 16,
      textAlign: 'center',
    },
    faqContainer: {
      backgroundColor: '#ffffff',
      borderRadius: 12,
      margin: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    faqHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 20,
      borderBottomWidth: 1,
      borderBottomColor: '#f3f4f6',
    },
    faqTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: '#1f2937',
    },
    faqContent: {
      padding: 20,
    },
    faqItem: {
      marginBottom: 20,
    },
    faqQuestion: {
      fontSize: 16,
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: 8,
    },
    faqAnswer: {
      fontSize: 14,
      color: '#6b7280',
      lineHeight: 20,
    },
  });

export default SimpleInterestCalculatorScreen;